import React, { useEffect, useState } from "react";
import AdminSideBar from "../../common/AdminSideBar/AdminSideBar";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useSelector, useDispatch } from "react-redux";
import { getAllRequests } from "../../redux/requestSlice";
import { getAllTherapistPayments } from "../../redux/therapistPeymentSlice";
import { getAdminPayments } from "../../redux/paymentSlice";
import { getTherapists } from "../../redux/therapistSlice";
import { getUsers } from "../../redux/userSlice";
import { FiTrendingUp, FiTrendingDown, FiDollarSign, FiCalendar, FiRefreshCw, FiActivity } from "react-icons/fi";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const parseAmount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const parseDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isRefunded = (req) =>
  req?.payment?.status === "refunded" ||
  req?.payment?.details?.refund?.status === "succeeded";

const getReqDate = (req) => {
  const slot = req?.selectedSlot && typeof req.selectedSlot === "object" ? req.selectedSlot : null;
  const raw =
    slot?.startDate ?? slot?.startTime ?? slot?.start ??
    req?.sessionDate ?? req?.startDate ?? req?.startTime ??
    req?.appointmentDate ?? req?.date ?? req?.createdAt;
  return parseDate(raw);
};

const getReqPrice = (req, hourlyPriceMap = {}) => {
  const slot = req?.selectedSlot && typeof req.selectedSlot === "object" ? req.selectedSlot : null;
  const tid = typeof req?.therapistId === "object" ? req?.therapistId?._id : req?.therapistId;
  return (
    parseAmount(slot?.price) ||
    parseAmount(req?.price) ||
    parseAmount(req?.payment?.amount) ||
    parseAmount(req?.payment?.details?.amount) ||
    hourlyPriceMap[tid] ||
    0
  );
};

const unpaidStatuses = ["pending", "cancelled", "canceled", "rejected"];
const MIN_REAL_PAYMENT = 50; // £1/£11 test ödemelerini filtrele

const isTestPayment = (p) => !p?.isFreeSession && parseAmount(p?.amount) < MIN_REAL_PAYMENT && parseAmount(p?.amount) > 0;

const isFreeSession = (req) =>
  req?.isComplimentary === true ||
  req?.isFreeSession === true ||
  req?.sessionType === "complimentary" ||
  req?.type === "complimentary" ||
  (typeof req?.description === "string" && req.description.startsWith("Free session"));

const isPaid = (req) =>
  !unpaidStatuses.includes(req?.status) && !isRefunded(req) && !isFreeSession(req);

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ─── COMPONENT ────────────────────────────────────────────────────────────────

const AdminPayment = () => {
  const dispatch = useDispatch();
  const { requests: allRequests = [] } = useSelector((s) => s.requests);
  const { therapistPayments = [] } = useSelector((s) => s.therapistPayments);
  const { adminPayments = [] } = useSelector((s) => s.payment);
  const { therapists = [] } = useSelector((s) => s.therapist);
  const { users = [] } = useSelector((s) => s.users);

  const [chartPeriod, setChartPeriod] = useState(6);
  const [txFilter, setTxFilter] = useState("all");
  const [expandedTx, setExpandedTx] = useState(null);

  useEffect(() => {
    dispatch(getAllRequests());
    dispatch(getAllTherapistPayments());
    dispatch(getAdminPayments());
    dispatch(getTherapists());
    dispatch(getUsers());
  }, [dispatch]);


  // ── Build lookup maps ──────────────────────────────────────────────────────
  const hourlyPriceMap = {};
  therapistPayments.forEach((tp) => {
    const tid = typeof tp.therapistId === "object" ? tp.therapistId?._id : tp.therapistId;
    if (tid && tp.hourlyPrice) hourlyPriceMap[tid] = parseAmount(tp.hourlyPrice);
  });
  therapists.forEach((t) => {
    if (!hourlyPriceMap[t._id]) {
      const rate = parseAmount(t.therapistDetail?.hourlyRate || t.therapistPayment?.hourlyPrice || t.hourlyPrice);
      if (rate) hourlyPriceMap[t._id] = rate;
    }
  });

  // availabilityId → payment map (gerçek Stripe verileri)
  const paymentByAvailId = {};
  adminPayments.forEach((p) => {
    const availId = typeof p.details?.availabilityId === "object"
      ? p.details.availabilityId.$oid || p.details.availabilityId._id
      : p.details?.availabilityId;
    if (availId) paymentByAvailId[availId] = p;
  });

  const getPaymentForRequest = (req) => {
    const availId = req.availabilityIds?.[0];
    const idStr = typeof availId === "object" ? availId._id || availId.$oid : availId;
    return idStr ? paymentByAvailId[idStr] : null;
  };

  const therapistNameMap = {};
  therapists.forEach((t) => {
    therapistNameMap[t._id] = `${t.name || ""} ${t.lastName || ""}`.trim() || "Unknown";
  });

  const userNameMap = {};
  users.forEach((u) => {
    userNameMap[u._id] = `${u.name || ""} ${u.lastName || ""}`.trim() || "Unknown";
  });

  // ── Compute summary metrics ────────────────────────────────────────────────
  const now = new Date();
  const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  // Gerçek payment verisi varsa ondan hesapla, yoksa request'ten fallback
  const useRealPayments = adminPayments.length > 0;

  const getPaymentDate = (p) => parseDate(p.appointmentDate?.$date || p.appointmentDate || p.createdAt?.$date || p.createdAt);

  const thisMonthPayments = adminPayments.filter((p) => {
    const d = getPaymentDate(p);
    return d && d >= startOfThisMonth && d < startOfNextMonth;
  });
  const lastMonthPayments = adminPayments.filter((p) => {
    const d = getPaymentDate(p);
    return d && d >= startOfLastMonth && d < startOfThisMonth;
  });

  const realPayments = (arr) => arr.filter((p) => p.status === "completed" && !p.isFreeSession && !isTestPayment(p));

  const monthlyIncome = useRealPayments
    ? realPayments(thisMonthPayments).reduce((s, p) => s + parseAmount(p.amount), 0)
    : allRequests.filter((r) => { const d = getReqDate(r); return d && d >= startOfThisMonth && d < startOfNextMonth && isPaid(r); }).reduce((s, r) => s + getReqPrice(r, hourlyPriceMap), 0);

  const lastMonthIncome = useRealPayments
    ? realPayments(lastMonthPayments).reduce((s, p) => s + parseAmount(p.amount), 0)
    : allRequests.filter((r) => { const d = getReqDate(r); return d && d >= startOfLastMonth && d < startOfThisMonth && isPaid(r); }).reduce((s, r) => s + getReqPrice(r, hourlyPriceMap), 0);

  const totalIncome = useRealPayments
    ? realPayments(adminPayments).reduce((s, p) => s + parseAmount(p.amount), 0)
    : allRequests.filter(isPaid).reduce((s, r) => s + getReqPrice(r, hourlyPriceMap), 0);

  const refundedAmount = useRealPayments
    ? thisMonthPayments.filter((p) => p.status === "refunded").reduce((s, p) => s + parseAmount(p.amount), 0)
    : 0;

  const thisMonthPaid = useRealPayments
    ? thisMonthPayments.filter((p) => p.status === "completed" && !p.isFreeSession)
    : allRequests.filter((r) => { const d = getReqDate(r); return d && d >= startOfThisMonth && d < startOfNextMonth && isPaid(r); });

  const thisMonthRefunded = useRealPayments
    ? thisMonthPayments.filter((p) => p.status === "refunded")
    : [];

  const thisMonthFree = useRealPayments
    ? thisMonthPayments.filter((p) => p.isFreeSession)
    : allRequests.filter((r) => { const d = getReqDate(r); return d && d >= startOfThisMonth && d < startOfNextMonth && isFreeSession(r); });

  const totalFreeSessions = useRealPayments
    ? adminPayments.filter((p) => p.isFreeSession).length
    : allRequests.filter(isFreeSession).length;

  const incomeTrend = lastMonthIncome === 0
    ? monthlyIncome > 0 ? 100 : 0
    : Math.round(((monthlyIncome - lastMonthIncome) / lastMonthIncome) * 100 * 10) / 10;

  // ── Build chart data (last N months) ──────────────────────────────────────
  const chartMonths = Array.from({ length: chartPeriod }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (chartPeriod - 1 - i), 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const chartIncomes = chartMonths.map(({ year, month }) => {
    const from = new Date(year, month, 1);
    const to = new Date(year, month + 1, 1);
    if (useRealPayments) {
      return adminPayments
        .filter((p) => {
          const d = getPaymentDate(p);
          return d && d >= from && d < to && p.status === "completed" && !p.isFreeSession && !isTestPayment(p);
        })
        .reduce((s, p) => s + parseAmount(p.amount), 0);
    }
    return allRequests
      .filter((r) => {
        const d = getReqDate(r);
        return d && d >= from && d < to && isPaid(r);
      })
      .reduce((s, r) => s + getReqPrice(r, hourlyPriceMap), 0);
  });

  const chartLabels = chartMonths.map(({ year, month }) =>
    `${MONTH_LABELS[month]} ${year !== now.getFullYear() ? year : ""}`
  );

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Income (£)",
        data: chartIncomes,
        backgroundColor: "rgba(99, 102, 241, 0.8)",
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `£${ctx.parsed.y.toLocaleString()}`,
        },
        backgroundColor: "#1e1b4b",
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: "#6b7280" } },
      y: {
        grid: { color: "rgba(0,0,0,0.05)" },
        ticks: { color: "#6b7280", callback: (v) => `£${v.toLocaleString()}` },
      },
    },
  };

  // ── Recent transactions ────────────────────────────────────────────────────
  const recentTransactions = [...allRequests]
    .filter((r) => {
      const d = getReqDate(r) || parseDate(r.createdAt);
      if (!d) return false;
      if (txFilter === "all") return true;
      const p = getPaymentForRequest(r);
      const isFree = p ? !!p.isFreeSession : isFreeSession(r);
      const isRef = p ? p.status === "refunded" : isRefunded(r);
      const computedStatus = isFree ? "free"
        : isRef ? "refunded"
        : p ? (p.status === "completed" ? "paid" : p.status)
        : r?.status || "pending";
      if (txFilter === "paid") return computedStatus === "paid";
      if (txFilter === "free") return computedStatus === "free";
      if (txFilter === "refunded") return computedStatus === "refunded";
      if (txFilter === "accepted") return computedStatus === "accepted" || computedStatus === "confirmed";
      return true;
    })
    .sort((a, b) => {
      const da = getReqDate(a) || parseDate(a.createdAt);
      const db = getReqDate(b) || parseDate(b.createdAt);
      return db - da;
    })
    .slice(0, 20)
    .map((r) => {
      const tid = typeof r.therapistId === "object" ? r.therapistId?._id : r.therapistId;
      const uid = typeof r.userId === "object" ? r.userId?._id : r.userId;
      const therapistName =
        (typeof r.therapistId === "object" && r.therapistId?.name)
          ? `${r.therapistId.name} ${r.therapistId.lastName || ""}`.trim()
          : therapistNameMap[tid] || "—";
      const userName =
        (typeof r.userId === "object" && r.userId?.name)
          ? `${r.userId.name} ${r.userId.lastName || ""}`.trim()
          : userNameMap[uid] || "—";
      const date = getReqDate(r) || parseDate(r.createdAt);
      const payment = getPaymentForRequest(r);
      const realAmount = payment ? parseAmount(payment.amount) : null;
      const slot = r?.selectedSlot && typeof r.selectedSlot === "object" ? r.selectedSlot : null;
      const explicitAmount = realAmount ||
        parseAmount(slot?.price) ||
        parseAmount(r?.price) ||
        parseAmount(r?.payment?.amount) ||
        parseAmount(r?.payment?.details?.amount);
      const amount = explicitAmount || getReqPrice(r, hourlyPriceMap);
      const isEstimated = !explicitAmount && amount > 0;
      const isFree = payment ? !!payment.isFreeSession : isFreeSession(r);
      const isRef = payment ? payment.status === "refunded" : isRefunded(r);
      const status = isFree
        ? "free"
        : isRef
        ? "refunded"
        : payment
        ? (payment.status === "completed" ? "paid" : payment.status)
        : r?.status || "pending";
      return { id: r._id, userName, therapistName, amount, isEstimated: isFree ? false : isEstimated, date, status, raw: r, payment };
    });

  // ── Stat card helper ───────────────────────────────────────────────────────
  const StatCard = ({ icon, label, value, sub, trend }) => (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="p-2 bg-indigo-50 rounded-lg">{icon}</div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-sm font-medium ${trend >= 0 ? "text-green-600" : "text-red-500"}`}>
            {trend >= 0 ? <FiTrendingUp className="w-4 h-4" /> : <FiTrendingDown className="w-4 h-4" />}
            {trend >= 0 ? "+" : ""}{trend}%
          </div>
        )}
      </div>
      <div>
        <p className="text-sm text-gray-500 mb-1">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      </div>
    </div>
  );

  const statusBadge = (status) => {
    const map = {
      paid: "bg-green-100 text-green-700",
      completed: "bg-green-100 text-green-700",
      free: "bg-blue-100 text-blue-600",
      refunded: "bg-red-100 text-red-600",
      pending: "bg-orange-100 text-orange-600",
      accepted: "bg-indigo-100 text-indigo-600",
      confirmed: "bg-indigo-100 text-indigo-600",
      active: "bg-teal-100 text-teal-600",
      ongoing: "bg-teal-100 text-teal-600",
      cancelled: "bg-gray-100 text-gray-500",
      canceled: "bg-gray-100 text-gray-500",
      rejected: "bg-gray-100 text-gray-500",
    };
    return (
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${map[status] || "bg-gray-100 text-gray-500"}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
      <div className="md:shrink-0 md:mr-6">
        <AdminSideBar />
      </div>

      <div className="flex-1 p-4 md:p-8 max-w-7xl w-full">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Payment Analytics</h1>
          <p className="text-gray-500 text-sm">Overview of platform revenue and transactions</p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-8">
          <StatCard
            icon={<FiDollarSign className="w-5 h-5 text-indigo-600" />}
            label="This Month's Income"
            value={`£${monthlyIncome.toLocaleString()}`}
            sub="vs last month"
            trend={incomeTrend}
          />
          <StatCard
            icon={<FiActivity className="w-5 h-5 text-indigo-600" />}
            label="Total Income"
            value={`£${totalIncome.toLocaleString()}`}
            sub="All time"
          />
          <StatCard
            icon={<FiCalendar className="w-5 h-5 text-indigo-600" />}
            label="Sessions This Month"
            value={thisMonthPaid.length}
            sub="Completed & paid"
          />
          <StatCard
            icon={<FiRefreshCw className="w-5 h-5 text-indigo-600" />}
            label="Refunded This Month"
            value={`£${refundedAmount.toLocaleString()}`}
            sub={`${thisMonthRefunded.length} sessions`}
          />
          <StatCard
            icon={<FiCalendar className="w-5 h-5 text-indigo-600" />}
            label="Free Sessions This Month"
            value={thisMonthFree.length}
            sub={`${totalFreeSessions} all time`}
          />
        </div>

        {/* Chart */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Monthly Revenue</h2>
              <p className="text-sm text-gray-400">Platform income by month</p>
            </div>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {[3, 6, 12].map((n) => (
                <button
                  key={n}
                  onClick={() => setChartPeriod(n)}
                  className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                    chartPeriod === n ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {n}M
                </button>
              ))}
            </div>
          </div>
          <div className="h-64">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>

        {/* Recent transactions */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Transactions</h2>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {[
                { key: "all", label: "All" },
                { key: "paid", label: "Paid" },
                { key: "free", label: "Free" },
                { key: "refunded", label: "Refunded" },
                { key: "accepted", label: "Accepted" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setTxFilter(key)}
                  className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${
                    txFilter === key ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {recentTransactions.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-8">No transactions yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-100">
                    <th className="pb-3 pr-4 font-medium">Date</th>
                    <th className="pb-3 pr-4 font-medium">User</th>
                    <th className="pb-3 pr-4 font-medium">Therapist</th>
                    <th className="pb-3 pr-4 font-medium">Amount</th>
                    <th className="pb-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTransactions.map((tx) => {
                    const isExpanded = expandedTx === tx.id;
                    const r = tx.raw;
                    const sessionStart = parseDate(r.meetingId?.startedAtDate) || parseDate(r.selectedSlot?.startDate) || parseDate(r.selectedSlot?.startTime);
                    const sessionEnd = parseDate(r.meetingId?.endedAtDate) || parseDate(r.selectedSlot?.endDate) || parseDate(r.selectedSlot?.endTime);
                    const p = tx.payment;
                    const paymentRef = p?.stripePaymentIntentId || p?.stripeSessionId || r.payment?.intentId || r.payment?.paymentIntentId;
                    const stripeAmount = p?.amount || r.payment?.amount || r.payment?.details?.amount;
                    const platformFee = p?.platformFee;
                    const therapistAmount = p?.therapistAmount;

                    return (
                      <React.Fragment key={tx.id}>
                        <tr
                          className="hover:bg-gray-50 transition-colors cursor-pointer border-t border-gray-50"
                          onClick={() => setExpandedTx(isExpanded ? null : tx.id)}
                        >
                          <td className="py-3 pr-4 text-gray-500 whitespace-nowrap">
                            {tx.date ? tx.date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                          </td>
                          <td className="py-3 pr-4 font-medium text-gray-700">{tx.userName}</td>
                          <td className="py-3 pr-4 text-gray-600">{tx.therapistName}</td>
                          <td className="py-3 pr-4">
                            {tx.status === "free"
                              ? <span className="text-gray-400">—</span>
                              : tx.amount > 0
                              ? <span className={tx.isEstimated ? "text-gray-400" : "font-semibold text-gray-900"}>
                                  {tx.isEstimated ? "Est. " : ""}£{tx.amount.toLocaleString()}
                                </span>
                              : "—"}
                          </td>
                          <td className="py-3 flex items-center gap-2">
                            {statusBadge(tx.status)}
                            <svg
                              className={`w-3.5 h-3.5 text-gray-400 transition-transform ml-auto ${isExpanded ? "rotate-180" : ""}`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr className="bg-indigo-50/40">
                            <td colSpan={5} className="px-4 py-4">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="text-xs text-gray-400 mb-1">Request ID</p>
                                  <p className="font-mono text-gray-600 text-xs break-all">{r._id || "—"}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-400 mb-1">Session Time</p>
                                  <p className="text-gray-700">
                                    {sessionStart
                                      ? sessionStart.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                                      : "—"}
                                    {sessionEnd && ` – ${sessionEnd.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-400 mb-1">Consultation Type</p>
                                  <p className="text-gray-700 capitalize">{r.consultationType || "—"}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-400 mb-1">Request Status</p>
                                  <p className="text-gray-700 capitalize">{r.status || "—"}</p>
                                </div>
                                {paymentRef && (
                                  <div>
                                    <p className="text-xs text-gray-400 mb-1">Payment Reference</p>
                                    <p className="font-mono text-gray-600 text-xs break-all">{paymentRef}</p>
                                  </div>
                                )}
                                {stripeAmount ? (
                                  <div>
                                    <p className="text-xs text-gray-400 mb-1">Stripe Amount</p>
                                    <p className="text-gray-700 font-semibold">£{parseAmount(stripeAmount).toLocaleString()}</p>
                                  </div>
                                ) : tx.isEstimated && (
                                  <div>
                                    <p className="text-xs text-gray-400 mb-1">Amount</p>
                                    <p className="text-yellow-600 text-xs">Estimated — no payment record found</p>
                                  </div>
                                )}
                                {platformFee != null && (
                                  <div>
                                    <p className="text-xs text-gray-400 mb-1">Platform Fee</p>
                                    <p className="text-gray-700">£{parseAmount(platformFee).toLocaleString()}</p>
                                  </div>
                                )}
                                {therapistAmount != null && (
                                  <div>
                                    <p className="text-xs text-gray-400 mb-1">Therapist Receives</p>
                                    <p className="text-gray-700">£{parseAmount(therapistAmount).toLocaleString()}</p>
                                  </div>
                                )}
                                {r.description && (
                                  <div className="col-span-2">
                                    <p className="text-xs text-gray-400 mb-1">Description</p>
                                    <p className="text-gray-600">
                                      {r.description
                                        .replace("Terapi seansı rezervasyonu", "Therapy session reservation")
                                        .replace("Ödeme ID:", "Payment ID:")}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPayment;
